import GridDrawer from '@/components/common/GridDrawer';
import {
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction
} from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import ToolTabs from '@/app/common/components/ToolTabs';
import { toolRoutes } from '@/routes/siteMaps';
import { useDispatch } from 'react-redux';
import RouteHelperAlerts from '@/components/common/RouteHelperAlerts';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import DataLoadError from '@/components/errors/DataLoadError';
import { shouldShowDataLoadError, isNetworkApiError } from '@/app/common/utils/apiErrorUtils';
import ToolsBreadcrumb from '@/app/common/components/ToolsBreadcrumb';
import CtrDrawer from './components/CtrDrawer';
import SubtleBadge from '@/components/common/SubtleBadge';
import SearchVolumeDrawer from './components/SearchVolumeDrawer';
import {
  PauseMassiveContext,
  RemoveClusteringContext,
  RemoveGscContext,
  RemoveMassiveContext,
  RemovePlannerContext
} from '@/context/Context';
import MassiveProviders from './Providers/MassiveProviders';
import ExpireCheck from '@/app/common/components/ExpireCheck';
import useDomain from '@/hooks/useDomain';
import ClusteringProviders from './Providers/ClusteringProviders';
import { removeFilters } from '../common/reducers/filterSlice';

type BreadcrumbItem = { text: string; href: string };

type OutletContext = [
  BreadcrumbItem[],
  Dispatch<SetStateAction<BreadcrumbItem[]>>
];

type NavRoute = {
  name: string;
  to: string;
  disabled?: boolean;
  helper?: ReactNode;
  children?: NavRoute[];
};

type NavSection = {
  name: string;
  children?: NavRoute[];
};

const KeywordResearchMain = () => {
  const { domain_slug, domain } = useDomain();
  const { isLoading, isError, error, refetch, isFetching } = useGetUserInfoQuery();
  const routes = (toolRoutes.children as NavSection[]).find(
    item => item.name === 'تحقیق کلمات'
  );
  const [breadcrumbData, setBreadcrumbData] = useState<BreadcrumbItem[]>([]);

  const location2 = useLocation();

  const isAiKeywordsPage =
    location2.pathname.includes('/ai-keywords/history') ||
    location2.pathname.includes('/ai-keywords/details');

  const isRouteActive = (
    route: NavRoute,
    pathname: string,
    navLinkActive = false
  ) => {
    const isAiKeywordsRoute =
      route.to === '/keyword-research/ai-keywords/history';
    const isAiKeywordsPage =
      pathname.includes('/ai-keywords/history') ||
      pathname.includes('/ai-keywords/details');
    if (navLinkActive || (isAiKeywordsRoute && isAiKeywordsPage)) {
      return true;
    }
    const isRelatedTab = route.to === '/keyword-research/massive';
    const isRelatedOrMassivePage =
      pathname.includes('/keyword-research/massive/') ||
      pathname.includes('/keyword-research/related/');
    if (isRelatedTab && isRelatedOrMassivePage) {
      return true;
    }
    const linkPath = `${route.to}/${domain_slug}/${domain}`;
    return pathname === linkPath || pathname.startsWith(`${linkPath}/`);
  };

  const navItems = routes;

  const activePage = useMemo(
    () =>
      navItems?.children?.find(route =>
        isRouteActive(route, location2.pathname)
      ),
    [navItems, location2.pathname, domain_slug, domain]
  );

  // REMOVE GSC CONTEXT
  const [domainGsc, setDomainGsc] = useState('');
  const [accountGsc, setAccountGsc] = useState('');
  const [hostNameIdGsc, setHostNameIdGsc] = useState('');
  const [showGscRem, setShowGscRem] = useState(false);

  // REMOVE MASSIVE CONTEXT
  const [massiveTaskId, setMassiveTaskId] = useState('');
  const [massiveKeywords, setMassiveKeywords] = useState<string[]>([]);
  const [showMassiveRem, setShowMassiveRem] = useState(false);

  // REMOVE PLANNER CONTEXT
  const [plannerTaskId, setPlannerTaskId] = useState('');
  const [plannerKeyword, setPlannerKeyword] = useState('');
  const [showPlannerRem, setShowPlannerRem] = useState(false);

  // REMOVE CLUSTERING CONTEXT
  const [clusteringTaskId, setClusteringTaskId] = useState('');
  const [clusteringKeywords, setClusteringKeywords] = useState('');
  const [showClusteringRem, setShowClusteringRem] = useState(false);

  // STOP MASSIVE CONTEXT
  const [delayModal, setDelayModal] = useState(false);
  const [showStopModal, setShowStopModal] = useState(false);
  const dispatch = useDispatch();

  if (!isLoading) {
    return (
      <ExpireCheck bypass={isAiKeywordsPage}>
        <ToolsBreadcrumb
          parentName="تحقیق کلمات"
          parentUrl={`/keyword-research/massive/${domain_slug}/${domain}`}
          childs={breadcrumbData}
        />
        <ToolTabs
          items={(navItems?.children ?? []).map(route => ({
            key: route.to,
            label: route.name,
            to: `${route.to}/${domain_slug}/${domain}`,
            disabled: route.disabled,
            state: { path: location2.pathname, query: null },
            active: isRouteActive(route, location2.pathname),
            onClick: () => {
              dispatch(removeFilters());
            },
            badge:
              route.to === '/keyword-research/ai-keywords/history' ? (
                <SubtleBadge bg="success" className="ms-1">
                  جدید
                </SubtleBadge>
              ) : undefined
          }))}
        />
        {activePage && activePage.to !== '/keyword-research/massive' && (
          <RouteHelperAlerts route={activePage} />
        )}
        <GridDrawer />
        <CtrDrawer />
        <SearchVolumeDrawer />
        {shouldShowDataLoadError(isError, error) ? (
          <DataLoadError
            onRetry={() => refetch()}
            isRetrying={isFetching}
            isNetworkError={isNetworkApiError(error)}
          />
        ) : (
          <RemoveGscContext.Provider
            value={{
              account: accountGsc,
              domain: domainGsc,
              hostNameId: hostNameIdGsc,
              setAccount: setAccountGsc,
              setDomain: setDomainGsc,
              setHostNameId: setHostNameIdGsc,
              show: showGscRem,
              setShow: setShowGscRem
            }}
          >
            <RemoveMassiveContext.Provider
              value={{
                keyword: massiveKeywords,
                setkeyword: setMassiveKeywords,
                setShow: setShowMassiveRem,
                show: showMassiveRem,
                setTaskId: setMassiveTaskId,
                task_id: massiveTaskId
              }}
            >
              <RemoveClusteringContext.Provider
                value={{
                  keyword: clusteringKeywords,
                  setkeyword: setClusteringKeywords,
                  setShow: setShowClusteringRem,
                  show: showClusteringRem,
                  setTaskId: setClusteringTaskId,
                  task_id: clusteringTaskId
                }}
              >
                <PauseMassiveContext.Provider
                  value={{
                    delayModal,
                    setDelayModal,
                    setShowStopModal,
                    showStopModal,
                    setTaskId: setMassiveTaskId,
                    task_id: massiveTaskId
                  }}
                >
                  <RemovePlannerContext.Provider
                    value={{
                      keyword: plannerKeyword,
                      setKeyword: setPlannerKeyword,
                      setShow: setShowPlannerRem,
                      show: showPlannerRem,
                      setTaskId: setPlannerTaskId,
                      taskId: plannerTaskId
                    }}
                  >
                    <MassiveProviders>
                      <ClusteringProviders>
                        <Outlet
                          context={
                            [
                              breadcrumbData,
                              setBreadcrumbData
                            ] satisfies OutletContext
                          }
                        />
                      </ClusteringProviders>
                    </MassiveProviders>
                  </RemovePlannerContext.Provider>
                </PauseMassiveContext.Provider>
              </RemoveClusteringContext.Provider>
            </RemoveMassiveContext.Provider>
          </RemoveGscContext.Provider>
        )}
      </ExpireCheck>
    );
  }

  return null;
};

export default KeywordResearchMain;
